import {
  mdiCheck,
  mdiClose,
  mdiPencil,
  mdiChevronDown,
  mdiChevronUp,
  mdiContentCopy,
} from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { FunctionComponent, useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { UTCToLocalDate } from "../../../../../utils/date_util";
import { InventoryStatus } from "../../../../../utils/enums";
import { BaseModel } from "../../../../../service/models/base";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";
import { ItemModelV2 } from "../../../../../erp/inventory/models/item_model";

export interface OrderDeviceV2 extends BaseModel {
  order_id: string;
  item_code: string;
  item?: ItemModelV2;
  status: string;
  item_name: string;
  device_id: string;

  warehouse_id?: string;
  warehouse?: WarehouseModel;

  installation_date: Date | null;
  return_back_date: Date | null;
  delivery_date: Date | null;

  item_id: string;
  active: boolean;
  org_namespace?: string;
  org_name?: string;
  replaced_to?: string;
  replaced_by?: string;
  outward_type?: string;
  voucher_id?: string;
  replaced_to_id?: string;
  replaced_by_id?: string;
  issue_category?: string;
}

export interface OrderDevice {
  id: string;
  order_id: string;
  device_id: string;
  status: string;
  product_id: number;
  product_name: string;
  created_by: any;
  created_at?: Date | null;
  created_by_name: string;
  updated_at?: Date | null;
  updated_by: any;
  updated_by_name: string;
  installation_date?: Date | null;
  return_back_date?: Date | null;
  delivery_date?: Date | null;
  item_id: string;
  active: boolean;
  org_namespace?: string;
  org_name?: string;
  replaced_to?: string;
  replaced_by?: string;
  replaced_to_id?: string;
  replaced_by_id?: string;
  issue_category?: string;
}
export const OrderDeviceDefaultValue: OrderDevice = {
  id: "",
  order_id: "",
  device_id: "",
  status: "",
  org_namespace: "",
  org_name: "",
  product_id: 0,
  product_name: "",
  created_at: new Date(),
  created_by: "",
  created_by_name: "",
  updated_at: new Date(),
  updated_by: "",
  updated_by_name: "",
  installation_date: new Date(),
  return_back_date: new Date(),
  delivery_date: new Date(),
  item_id: "",
  active: false,
};
interface OrderDeviceRowProps {
  device: OrderDeviceV2;
  sl_no: number;
  onUpdate: (data: OrderDeviceV2) => void;
  canEdit?: boolean;
  isEditAllowed?: boolean;
  disabled?: boolean;
}

const OrderDeviceRow: FunctionComponent<OrderDeviceRowProps> = (props) => {
  const [edit, setEdit] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [device, setDevice] = useState({ ...props.device });
  const { showToast } = useToast();
  const { isEditAllowed = false } = props;

  useEffect(() => {
    if (!isEditAllowed) {
      setEdit(false);
      setDevice({ ...props.device });
    }
  }, [isEditAllowed, props.device]);

  const canEditRow = isEditAllowed && edit;

  return (
    <div className={`bg-white rounded-md px-3 py-2 text-xs`}>
      <div className="grid grid-cols-1 gap-1.5 md:gap-0 md:grid-cols-12 md:items-center md:justify-center">
        {/* <div>{props.sl_no}</div> */}
        <div className="flex justify-between md:block">
          <span className="md:hidden text-gray-400">ID</span>
          <span>
            {device.id ?? ""} | {device.item_code ?? ""}
          </span>
        </div>
        <div className="flex justify-between md:justify-start gap-2">
          <span className="md:hidden text-gray-400">Device Id</span>
          <div className="flex">
            <button
              onClick={() => {
                navigator.clipboard.writeText(device.device_id ?? "");
                showToast({
                  type: ToastType.info,
                  text: "Copied to clipboard !",
                });
              }}
              className=""
            >
              <Icon path={mdiContentCopy} className="w-3 h-3"></Icon>
            </button>
            <div
              className={`${
                device.status === InventoryStatus.returnedNStocked
                  ? "text-red-400"
                  : "text-green-400"
              }`}
            >
              {device.device_id}
            </div>
          </div>
        </div>
        <div className="flex justify-between md:flex-col gap-1">
          <span className="md:hidden text-gray-400">Warehouse</span>
          <span>{device.warehouse?.name ?? "---"}</span>
        </div>
        <div className="flex justify-between md:block md:text-center">
          <span className="md:hidden text-gray-400">Outward Type</span>
          <span>{device.outward_type ?? "---"}</span>
        </div>
        <div className="flex justify-between md:block md:text-center">
          <span className="md:hidden text-gray-400">Voucher</span>
          <span>{device.voucher_id ?? "---"}</span>
        </div>
        {/* <div className="flex flex-col gap-1">
          <p>{device.org_namespace ?? "---"}</p>
        </div> */}
        <div className="flex justify-between md:flex-col gap-1 md:max-w-xs">
          <span className="md:hidden text-gray-400">Org Namespace</span>
          <p className="break-words whitespace-pre-wrap text-right md:text-left">
            {device.org_namespace ?? "---"}
          </p>
        </div>

        <div className="flex justify-between md:block">
          <span className="md:hidden text-gray-400">Item</span>
          <span>{device.item_name}</span>
        </div>

        <div className="flex justify-between items-center md:block">
          <span className="md:hidden text-gray-400">Delivery Date</span>
          <input
            disabled={!canEditRow}
            name="date"
            type="date"
            id="date"
            className="p-1 rounded bg-gray-100 focus:outline-none"
            value={
              device.delivery_date
                ? moment(device.delivery_date).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) => {
              setDevice((o) => ({
                ...o,
                delivery_date: UTCToLocalDate(e.target.value)!,
              }));
            }}
          />
        </div>
        <div className="flex justify-between items-center md:block">
          <span className="md:hidden text-gray-400">Installation Date</span>
          <input
            disabled={!canEditRow}
            name="date"
            type="date"
            id="date"
            className="p-1 rounded bg-gray-100 focus:outline-none"
            value={
              device.installation_date
                ? moment(device.installation_date).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) => {
              setDevice((o) => ({
                ...o,
                installation_date: UTCToLocalDate(e.target.value)!,
              }));
            }}
          />
        </div>
        <div className="flex justify-between items-center md:block">
          <span className="md:hidden text-gray-400">Return Date</span>
          <input
            disabled={!canEditRow}
            name="date"
            type="date"
            id="date"
            className="p-1 rounded bg-gray-100 focus:outline-none"
            value={
              device.return_back_date
                ? moment(device.return_back_date).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) => {
              setDevice((o) => ({
                ...o,
                return_back_date: UTCToLocalDate(e.target.value)!,
              }));
            }}
          />
        </div>

        <div className="flex justify-between items-center md:block">
          <span className="md:hidden text-gray-400">Status</span>
          <select
            disabled={!canEditRow}
            name=""
            id=""
            className="p-1 rounded bg-gray-100 focus:outline-none"
            value={device.status}
            onChange={(e) =>
              setDevice((o) => ({ ...o, status: e.target.value }))
            }
          >
            <option value="" hidden>
              select
            </option>
            {[
              InventoryStatus.delivered,
              InventoryStatus.installed,
              InventoryStatus.returnedNStocked,
              InventoryStatus.stockOut,
            ].map((s, i) => (
              <option value={s} key={i}>
                {s}
              </option>
            ))}
          </select>
        </div>
        {props.isEditAllowed && !props.disabled && !props.canEdit && (
          <div className="">
            <>
              <div className="flex justify-center gap-2">
                {canEditRow ? (
                  <>
                    <div
                      onClick={() => {
                        if (!window.confirm("Are you sure !")) return;
                        props.onUpdate(device);
                        setEdit(false);
                      }}
                      className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                    >
                      <Icon path={mdiCheck} className="h-4 w-4"></Icon>
                    </div>
                    <div
                      onClick={() => {
                        setEdit(false);
                        setDevice({ ...props.device });
                      }}
                      className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200"
                    >
                      <Icon path={mdiClose} className="h-4 w-4"></Icon>
                    </div>
                  </>
                ) : (
                  <div
                    onClick={() => props.onUpdate(device)}
                    className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                  >
                    <Icon path={mdiPencil} className="h-4 w-4"></Icon>
                  </div>
                )}{" "}
                {props.device && (
                  <div
                    onClick={() => setShowMore((o) => !o)}
                    className="p-1 bg-gray-100 rounded cursor-pointer hover:bg-gray-200"
                  >
                    <Icon
                      path={showMore ? mdiChevronUp : mdiChevronDown}
                      className="h-4 w-4"
                    ></Icon>
                  </div>
                )}
              </div>
            </>
          </div>
        )}
      </div>
      {showMore && props.device && (
        <div className=" mt-2">
          <hr className=" " />

          <div className="flex flex-col gap-2 my-1">
            <p className="text-xs ">
              Created by :<br />
              <div className="flex gap-1">
                <div className="">
                  <b>{props.device.created_by_name ?? "N/A"}</b>
                </div>
                <div className="">
                  {props.device.created_at
                    ? moment(props.device.created_at).format(
                        "DD MMM YYYY h:mm a",
                      )
                    : "--"}
                </div>
              </div>
            </p>
            <p className="text-xs ">
              Updated by :<br />
              <div className="flex gap-1">
                <div className="">
                  <b>{props.device.updated_by_name ?? "N/A"}</b>
                </div>
                <div className="">
                  {props.device.updated_at
                    ? moment(props.device.updated_at).format(
                        "DD MMM YYYY h:mm a",
                      )
                    : "--"}
                </div>
              </div>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDeviceRow;
